import React from "react";
import { Layout } from "../../layouts/Layout";
import { BlogPageWrapper } from "../../components/BlogAreas/BlogPageWrapper";
import { Link, Navigate, useParams } from "react-router-dom";
import { BLOG_POSTS, COMPANY, SERVICES } from "../../content/siteContent";
import { getBlogSeo } from "../../components/SEO/Seo";
import { BlogAuthorInfo } from "../../components/BlogAreas/BlogAuthorInfo";

const formatDate = (isoDate) =>
  new Date(isoDate).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const BlogContentBlock = ({ block }) => {
  if (block.type === "h2") {
    return <h4 className="title-two">{block.text}</h4>;
  }
  if (block.type === "ul") {
    return (
      <ul className="list-wrap blog-content-list">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p>{block.text}</p>;
};

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((item) => item.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedService = SERVICES.find(
    (service) => service.slug === post.relatedService
  );
  const blogSeo = getBlogSeo(post.slug);
  const otherPosts = BLOG_POSTS.filter((item) => item.slug !== post.slug)
    .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
    .slice(0, 2);

  return (
    <Layout
      breadcrumb={"Blog Details"}
      title={post.title}
      seo={{
        title: blogSeo.title,
        description: blogSeo.description,
        pathname: `/blog/${post.slug}`,
        schemaType: "article",
      }}
    >
      <BlogPageWrapper>
        {/* image */}
        <div className="blog-details-thumb">
          <img
            src={`${process.env.PUBLIC_URL}/images/services-details/${post.image}`}
            alt={post.imageAlt}
            loading="lazy"
          />
        </div>

        {/* content */}
        <div className="blog-details-content">
          <h2 className="title">{post.title}</h2>

          <div className="blog-meta-three">
            <ul className="list-wrap">
              <li>
                <i className="far fa-calendar"></i>
                {formatDate(post.publishDate)}
              </li>
              <li>
                <img src="/images/team/faisal.png" alt="Faisal Shabbir" />
                by Faisal Shabbir
              </li>
              <li>
                <i className="fas fa-tags"></i>
                {post.tags.map((tag, idx) => (
                  <span key={tag}>
                    {tag}
                    {idx < post.tags.length - 1 ? ", " : ""}
                  </span>
                ))}
              </li>
              <li>{post.readTime}</li>
            </ul>
          </div>

          {post.content.map((block, idx) => (
            <BlogContentBlock block={block} key={idx} />
          ))}

          {post.faqs && post.faqs.length > 0 && (
            <>
              <h4 className="title-two">Frequently Asked Questions</h4>
              {post.faqs.map(([question, answer]) => (
                <div className="mb-25" key={question}>
                  <h6 className="mb-10">{question}</h6>
                  <p>{answer}</p>
                </div>
              ))}
            </>
          )}

          {relatedService && (
            <blockquote>
              <p>
                Want help with {relatedService.title.toLowerCase()}? {" "}
                <Link to={`/services/${relatedService.slug}`}>
                  See how Zawix can help
                </Link>{" "}
                or{" "}
                <a href={COMPANY.whatsappHref}>message us on WhatsApp</a> for
                a free consultation.
              </p>
            </blockquote>
          )}

          <div className="bd-content-bottom">
            <div className="row align-items-center">
              <div className="col-md-7">
                <div className="post-tags">
                  <h5 className="title">Tags:</h5>
                  <ul className="list-wrap">
                    {post.tags.map((tag) => (
                      <li key={tag}>
                        <Link to={`/blog?tag=${encodeURIComponent(tag)}`}>
                          {tag}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="col-md-5">
                <div className="blog-post-share">
                  <h5 className="title">Share:</h5>
                  <ul className="list-wrap">
                    <li>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          `${process.env.REACT_APP_SITE_URL || "https://www.zawixbusinessconsultants.com"}/blog/${post.slug}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fab fa-facebook-f"></i>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* author info */}
        <BlogAuthorInfo />

        {/* related posts */}
        {otherPosts.length > 0 && (
          <div className="blog-widget">
            <h4 className="bw-title">More Guides</h4>
            <div className="rc-post-wrap">
              {otherPosts.map((item) => (
                <div key={item.id} className="rc-post-item">
                  <div className="thumb">
                    <Link to={`/blog/${item.slug}`}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/services-details/${item.image}`}
                        alt={item.imageAlt}
                      />
                    </Link>
                  </div>
                  <div className="content">
                    <span className="date">
                      <i className="far fa-calendar"></i>
                      {formatDate(item.publishDate)}
                    </span>
                    <h2 className="title">
                      <Link to={`/blog/${item.slug}`}>{item.title}</Link>
                    </h2>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </BlogPageWrapper>
    </Layout>
  );
};

export default BlogDetailsPage;

